import { postOauthgetToken } from 'api';
import TokenService from 'api/tokenService';
import AWS from 'aws-sdk';
import axios from 'axios';
import configuration from 'config/index';
import toast from 'react-hot-toast';

export const NESTJS_ENDPOINTS: Array<{
  path: string;
  method: string;
}> = [
  {
    path: '/vendors',
    method: 'post'
  },
  {
    path: '/vendors/:id',
    method: 'patch'
  },
  {
    path: '/jobs',
    method: 'post'
  },
  {
    path: '/jobs/:id',
    method: 'patch'
  },
  {
    path: '/vendors/:id/approve',
    method: 'patch'
  }
];

// =======================
// Helper Functions
// =======================

// Strip query parameters
function stripQueryParams(url: string): string {
  return url.split('?')[0];
}

// Convert path with :params to regex
function pathToRegex(path: string): RegExp {
  const regexPath = path.replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${regexPath}$`);
}

// =======================
// TOKEN REFRESH HELPERS
// =======================

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeToTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onRefreshed(newToken: string) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
  isRefreshing = false;
}

// =======================
// AXIOS INSTANCE
// =======================

const instance = axios.create({
  baseURL: configuration.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

// =======================
// REQUEST INTERCEPTOR
// =======================

instance.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const url = config.url ?? '';
    const method = config.method?.toLowerCase();

    if (!url.startsWith('http') && method) {
      const cleanUrl = stripQueryParams(url);

      const isNestJsEndpoint = NESTJS_ENDPOINTS.some((endpoint) => {
        if (endpoint.method.toLowerCase() !== method) return false;
        const endpointRegex = pathToRegex(endpoint.path);
        return endpointRegex.test(cleanUrl);
      });
      config.baseURL = isNestJsEndpoint
        ? configuration.apiBaseUrl_nestjs
        : configuration.apiBaseUrl;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =======================
// RESPONSE INTERCEPTOR
// =======================

instance.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalConfig = err.config;

    if (
      originalConfig?.url !== '/oauth2/token' &&
      err.response &&
      [401, 403].includes(err.response.status) &&
      !originalConfig._retry
    ) {
      originalConfig._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const tokenParams = new URLSearchParams();
          tokenParams.append('grant_type', 'refresh_token');
          tokenParams.append('client_id', configuration.awsClientId);
          tokenParams.append('refresh_token', TokenService.getRefreshToken());

          const tokenRes = await postOauthgetToken(tokenParams, true);

          TokenService.updateAccessToken(tokenRes.access_token);
          onRefreshed(tokenRes.access_token);

          // AWS credentials refresh
          AWS.config.region = configuration.awsRegion;
          AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: configuration.awsIdentityPoolId,
            Logins: {
              [`cognito-idp.${configuration.awsRegion}.amazonaws.com/${configuration.awsUserPoolId}`]:
                tokenRes.id_token
            }
          });

          (AWS.config.credentials as AWS.CognitoIdentityCredentials).get(() => {
            localStorage.setItem('accessKeyId', AWS.config.credentials?.accessKeyId ?? '');
            localStorage.setItem('secretAccessKey', AWS.config.credentials?.secretAccessKey ?? '');
            localStorage.setItem('sessionToken', AWS.config.credentials?.sessionToken ?? '');
          });

          originalConfig.headers.Authorization = `Bearer ${tokenRes.access_token}`;
          return axios(originalConfig);
        } catch (error) {
          return Promise.reject(error);
        }
      }

      return new Promise((resolve) => {
        subscribeToTokenRefresh((newToken: string) => {
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
          resolve(axios(originalConfig));
        });
      });
    }

    // Error handling
    if (err.response?.data?.error?.includes('Main project has no QuickBooks ID')) {
      toast.remove();
    } else {
      toast.error(err.response?.data?.error ?? err.message, {
        position: 'top-center'
      });
    }

    if (err.response?.status === 403) {
      toast.remove();
      localStorage.clear();
      window.location.href = '/home';
    }

    return Promise.reject(err);
  }
);

export default instance;
